import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Clock, PhoneOff, Plus, Timer } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { portalApi } from "@/features/customers/api";
import { money, onDay, perMinute } from "@/features/customers/format";
import {
  MinutesBar,
  fmtMinutes,
  minuteLevel,
  minuteTextClass,
} from "@/features/customers/minutes";
import { useAuthStore } from "@/features/auth/store";
import { cn } from "@/lib/utils";

/** What the customer bought and what is left of it.
 *
 * The one page in the dashboard that exists only for a customer: a tenant user
 * has no plan, so there is nothing here to share with them. It answers the two
 * questions a customer would otherwise have to email their provider about —
 * how many minutes are left, and when they reset — and warns before calls stop
 * rather than after.
 *
 * Read-only by design: only the provider can add minutes (Peter, 2026-07-26).
 */
export default function MyPlanPage() {
  const user = useAuthStore((s) => s.user);
  const providerName = user?.tenant?.name || "your provider";
  const q = useQuery({
    queryKey: ["my-plan"],
    queryFn: portalApi.myPlan,
    // Minutes move while the page is open; a stale allowance is the one number
    // that must not be stale.
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });

  if (user && user.role !== "customer") return <Navigate to="/dashboard" replace />;

  if (q.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-40" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (q.isError || !q.data) {
    return (
      <p className="p-8 text-center text-sm text-muted-foreground">
        Could not load your plan. Please try again.
      </p>
    );
  }

  const plan = q.data;
  const level = minuteLevel(plan);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">My Plan</h1>
        <p className="text-sm text-muted-foreground">
          Your call minutes for this period, and what happens when they run out.
        </p>
      </div>

      {!plan.metered ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            You are not on a plan yet, so your calls are not limited.{" "}
            {providerName} sets this up.
          </CardContent>
        </Card>
      ) : (
        <>
          {level === "empty" && (
            <Notice
              tone="destructive"
              icon={PhoneOff}
              title="Your call minutes are used up — new calls are being refused"
            >
              Minutes reset on {onDay(plan.period_end)}. Ask {providerName} to add
              minutes to start taking calls again before then.
            </Notice>
          )}
          {(level === "warn" || level === "critical") && (
            <Notice
              tone="warning"
              icon={AlertTriangle}
              title={`${fmtMinutes(plan.minutes_left)} call minutes left`}
            >
              Calls stop once they run out, until {onDay(plan.period_end)}. Ask{" "}
              {providerName} if you need more this period.
            </Notice>
          )}

          <Card>
            <CardContent className="space-y-5 p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Your plan
                  </p>
                  <p className="text-lg font-semibold">{plan.plan?.name}</p>
                  {plan.plan?.description && (
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {plan.plan.description}
                    </p>
                  )}
                </div>
                {plan.plan && (
                  <div className="text-right">
                    <p className="text-lg font-semibold">
                      {money(plan.plan.price_cents)}
                      <span className="text-xs font-normal text-muted-foreground">
                        {" "}
                        /month
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {fmtMinutes(plan.plan.included_minutes)} minutes included ·{" "}
                      {perMinute(plan.plan.rate_per_minute_cents)}/min
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-2 border-t pt-5">
                <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                  <p className="text-3xl font-semibold tabular-nums">
                    {fmtMinutes(plan.minutes_left)}
                    <span className="ml-1.5 text-sm font-normal text-muted-foreground">
                      minutes left
                    </span>
                  </p>
                  <p className={cn("text-sm tabular-nums", minuteTextClass(level))}>
                    {fmtMinutes(plan.minutes_used)} of{" "}
                    {fmtMinutes(plan.minutes_allowed)} used ({plan.percent_used}%)
                  </p>
                </div>
                <MinutesBar usage={plan} className="h-2" />
                <div className="flex flex-wrap gap-x-6 gap-y-1 pt-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <Timer className="h-3.5 w-3.5" />
                    {fmtMinutes(plan.included_minutes)} from your plan
                    {plan.extra_minutes > 0 &&
                      ` + ${fmtMinutes(plan.extra_minutes)} added`}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    This period: {onDay(plan.period_start)} –{" "}
                    {onDay(plan.period_end)}
                  </span>
                </div>
              </div>

              <p className="border-t pt-4 text-xs text-muted-foreground">
                Every minute of every call counts, rounded up to the next minute —
                including calls you make from the dashboard to test. Minutes reset
                on {onDay(plan.period_end)}; minutes {providerName} adds on top
                carry over.
              </p>
            </CardContent>
          </Card>

          {plan.minute_grants.length > 0 && (
            <Card>
              <CardContent className="p-0">
                <div className="border-b px-4 py-3">
                  <p className="text-sm font-medium">Minutes added</p>
                  <p className="text-xs text-muted-foreground">
                    On top of your plan, by {providerName}.
                  </p>
                </div>
                <ul className="divide-y">
                  {plan.minute_grants.map((g) => (
                    <li
                      key={g.id}
                      className="flex items-baseline justify-between gap-4 px-4 py-2.5 text-sm"
                    >
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 font-medium tabular-nums",
                          g.minutes < 0 && "text-muted-foreground",
                        )}
                      >
                        {g.minutes > 0 && <Plus className="h-3.5 w-3.5" />}
                        {fmtMinutes(g.minutes)} min
                      </span>
                      <span className="flex-1 truncate text-muted-foreground">
                        {g.note}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {onDay(g.created_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function Notice({
  tone,
  icon: Icon,
  title,
  children,
}: {
  tone: "destructive" | "warning";
  icon: typeof AlertTriangle;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex gap-3 rounded-lg border p-4 text-sm",
        tone === "destructive"
          ? "border-destructive/40 bg-destructive/10"
          : "border-amber-500/40 bg-amber-50 dark:bg-amber-950/30",
      )}
    >
      <Icon
        className={cn(
          "mt-0.5 h-4 w-4 shrink-0",
          tone === "destructive"
            ? "text-destructive"
            : "text-amber-600 dark:text-amber-400",
        )}
      />
      <div>
        <p
          className={cn(
            "font-medium",
            tone === "destructive"
              ? "text-destructive"
              : "text-amber-700 dark:text-amber-300",
          )}
        >
          {title}
        </p>
        <p className="mt-0.5 text-muted-foreground">{children}</p>
      </div>
    </div>
  );
}
